// Swiper

// Swiper Core
import Swiper from "swiper";
import { Pagination, Navigation } from "swiper/modules";

// Styles – do not import "swiper/css/navigation" or Swiper injects its own arrow icons
import "swiper/css";
import "swiper/css/pagination";

// GSAP

import { gsap } from "gsap";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(SplitText);

const queryInSwiper = (swiper, selector) => {
  if (!selector || !(swiper?.el instanceof Element)) return null;
  return swiper.el.querySelector(selector);
};

const resolveNavButtonEl = (raw, swiper) => {
  if (!raw) return null;
  if (raw instanceof Element) return raw;
  if (typeof raw === "string") {
    return queryInSwiper(swiper, raw) || document.querySelector(raw);
  }
  if (typeof raw === "object" && raw.length && raw[0] instanceof Element) {
    return raw[0];
  }
  return null;
};

const disablePrevUntilNextUsed = (swiper) => {
  if (!swiper || typeof swiper.on !== "function") return;
  if (!(swiper.el instanceof Element)) return;

  const getButtons = () => {
    const prevEl =
      resolveNavButtonEl(swiper.navigation?.prevEl, swiper) ||
      queryInSwiper(swiper, ".swiper-button-prevy, .swiper-button-prev");
    const nextEl =
      resolveNavButtonEl(swiper.navigation?.nextEl, swiper) ||
      queryInSwiper(swiper, ".swiper-button-nexty, .swiper-button-next");
    return { prevEl, nextEl };
  };

  const setDisabled = (el, disabled, className) => {
    if (!el) return;
    el.classList.toggle(className, disabled);
    if (disabled) {
      el.setAttribute("aria-disabled", "true");
      el.setAttribute("tabindex", "-1");
    } else {
      el.removeAttribute("aria-disabled");
      el.removeAttribute("tabindex");
    }
  };

  const updateNavState = () => {
    const { prevEl, nextEl } = getButtons();
    const locked = Boolean(swiper.isLocked);

    setDisabled(prevEl, !locked && swiper.isBeginning, "swiper-prev-initially-disabled");
    setDisabled(nextEl, !locked && swiper.isEnd, "swiper-next-at-end-disabled");

    const navBar = queryInSwiper(swiper, ".swiper-pagination")?.parentElement;
    if (navBar && swiper.el.contains(navBar)) {
      navBar.classList.toggle("swiper-nav-locked", locked);
    }
  };

  swiper.on("init", updateNavState);
  swiper.on("slideChange", updateNavState);
  swiper.on("reachBeginning", updateNavState);
  swiper.on("reachEnd", updateNavState);
  swiper.on("fromEdge", updateNavState);
  swiper.on("lock", updateNavState);
  swiper.on("unlock", updateNavState);
  swiper.on("resize", updateNavState);
  swiper.on("breakpoint", updateNavState);

  updateNavState();
};

const testimonialSwiper = () => {
  const el = document.querySelector(".testimonials");
  if (!el) return;

  const slideCount = el.querySelectorAll(".swiper-slide").length;
  const hasMultiple = slideCount > 1;

  const swiper = new Swiper(".testimonials", {
    modules: [Navigation, Pagination],
    loop: hasMultiple,
    speed: 500,
    effect: "fade",
    slidesPerView: 1,
    pagination: {
      el: ".swiper-pagination",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".swiper-button-nexty",
      prevEl: ".swiper-button-prevy",
    },
  });
  disablePrevUntilNextUsed(swiper);

  swiper.on("slideChange", function () {
    var currentSlide =
      swiper.slides[swiper.activeIndex].querySelector(".split-text");
    var splitText = new SplitText(currentSlide, { type: "lines" });
    var tl = gsap.timeline();

    tl.from(splitText.lines, {
      opacity: 0,
      y: 50,
      ease: "power3.out",
      stagger: 0.1,
      duration: 1,
      delay: 0.25,
    });

  });
};

export { testimonialSwiper };

const newsSwiper = () => {
  if (!document.querySelector(".news")) return;

  const newsswiper = new Swiper(".news", {
    modules: [Pagination, Navigation],
    draggable: true,
    slidesPerView: 1,
    spaceBetween: 20,
    breakpoints: {
      1200: {
        slidesPerView: 2.5,
      },
    },
    pagination: {
      el: ".swiper-pagination-news",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".swiper-button-next-news",
      prevEl: ".swiper-button-prev-news",
    },
  });
  disablePrevUntilNextUsed(newsswiper);
};

export { newsSwiper };

const articleSwiper = () => {
  if (!document.querySelector(".related-articles")) return;

  const articleswiper = new Swiper(".related-articles", {
    modules: [Pagination, Navigation],
    draggable: true,
    slidesPerView: 1,
    spaceBetween: 20,
    breakpoints: {
      768: {
        slidesPerView: 1.5,
      },
      1200: {
        slidesPerView: 2.5,
      },
    },
    pagination: {
      el: ".swiper-pagination-news",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".swiper-button-next-news",
      prevEl: ".swiper-button-prev-news",
    },
  });
  disablePrevUntilNextUsed(articleswiper);
};

export { articleSwiper };

const multipleTestimonialsSwiper = () => {
  const el = document.querySelector(".testimonials-multiple");
  if (!el) return;

  const multipletestimonialswiper = new Swiper(el, {
    modules: [Pagination, Navigation],
    draggable: true,
    watchOverflow: true,
    slidesPerView: 1.1,
    spaceBetween: 20,
    breakpoints: {
      768: {
        slidesPerView: 1.5,
      },
      1200: {
        slidesPerView: 2.5,
      },
    },
    pagination: {
      el: ".testimonials-multiple .swiper-pagination",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".testimonials-multiple .swiper-button-nexty",
      prevEl: ".testimonials-multiple .swiper-button-prevy",
    },
  });
  disablePrevUntilNextUsed(multipletestimonialswiper);
};

export { multipleTestimonialsSwiper };

const featuredSwiper = () => {
  if (!document.querySelector(".featured-articles")) return;

  const featuredswiper = new Swiper(".featured-articles", {
    modules: [Pagination, Navigation],
    draggable: true,
    slidesPerView: 1,
    pagination: {
      el: ".swiper-pagination-featured",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".swiper-button-next-featured",
      prevEl: ".swiper-button-prev-featured",
    },
  });
  disablePrevUntilNextUsed(featuredswiper);
};

export { featuredSwiper };

const gallerySwiper = () => {
  const galleryEl = document.querySelector(".gallery-swiper");
  if (!galleryEl) return;

  const galleryswiper = new Swiper(".gallery-swiper", {
    modules: [Pagination, Navigation],
    draggable: true,
    slidesPerView: 1,
    pagination: {
      el: ".swiper-pagination-gallery",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".swiper-button-next-gallery",
      prevEl: ".swiper-button-prev-gallery",
    },
  });
  disablePrevUntilNextUsed(galleryswiper);
};

export { gallerySwiper };

const subpageSwiper = () => {
  if (!document.querySelector(".subpage-carousel")) return;

  const subpageswiper = new Swiper(".subpage-carousel", {
    modules: [Pagination, Navigation],
    draggable: true,
    slidesPerView: 1,
    breakpoints: {
      1200: {
        slidesPerView: 2.5,
      },
    },
    pagination: {
      el: ".subpage-carousel .swiper-pagination",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".subpage-carousel .swiper-button-nexty",
      prevEl: ".subpage-carousel .swiper-button-prevy",
    },
  });
  disablePrevUntilNextUsed(subpageswiper);
};

export { subpageSwiper };

const categorySwiper = () => {
  if (!document.querySelector(".supplier-categories")) return;

  const categorySwiper = new Swiper(".supplier-categories", {
    modules: [Navigation],
    draggable: true,
    slidesPerView: 3,
    spaceBetween: 5,
    loop: true,
    breakpoints: {
      1200: {
        spaceBetween: 10,
        slidesPerView: 7,
      },
    },
    navigation: {
      nextEl: ".custom-swiper-button.swiper-button-next",
      prevEl: ".custom-swiper-button.swiper-button-prev",
    },
  });
  disablePrevUntilNextUsed(categorySwiper);
};

export { categorySwiper };

const featuredSupplierSwiper = () => {
  if (!document.querySelector(".featured-supplier-carousel")) return;

  const featuredSupplierSwiper = new Swiper(".featured-supplier-carousel", {
    modules: [Navigation, Pagination],
    draggable: true,
    slidesPerView: 1,
    spaceBetween: 20,
    centeredSlides: true,
    loop: true,
    breakpoints: {
      1200: {
        slidesPerView: 2.5,
      },
    },
    pagination: {
      el: ".featured-supplier-carousel .swiper-pagination",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".featured-supplier-carousel .swiper-button-nexty",
      prevEl: ".featured-supplier-carousel .swiper-button-prevy",
    },
  });
  disablePrevUntilNextUsed(featuredSupplierSwiper);
};

export { featuredSupplierSwiper };

const relatedSupplierSwiper = () => {
  if (!document.querySelector(".related-suppliers-carousel")) return;

  const relatedSupplierSwiper = new Swiper(".related-suppliers-carousel", {
    modules: [Navigation, Pagination],
    draggable: true,
    slidesPerView: 1,
    spaceBetween: 20,
    breakpoints: {
      1200: {
        slidesPerView: 2.5,
      },
    },
    pagination: {
      el: ".related-suppliers-carousel .swiper-pagination",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".related-suppliers-carousel .swiper-button-nexty",
      prevEl: ".related-suppliers-carousel .swiper-button-prevy",
    },
  });
  disablePrevUntilNextUsed(relatedSupplierSwiper);
};

export { relatedSupplierSwiper };

const featuresSwiper = () => {
  if (!document.querySelector(".features-carousel")) return;

  const featuresswiper = new Swiper(".features-carousel", {
    modules: [Pagination, Navigation],
    draggable: true,
    slidesPerView: 1,
    pagination: {
      el: ".features-swiper-pagination",
      type: "progressbar",
    },
    navigation: {
      nextEl: ".swiper-button-next-features",
      prevEl: ".swiper-button-prev-features",
    },
  });
  disablePrevUntilNextUsed(featuresswiper);

  // Function to animate elements on slide change
  featuresswiper.on("slideChange", function () {
    var currentSlide = featuresswiper.slides[featuresswiper.activeIndex];

    var splitTextElement = currentSlide.querySelector(".split-text");
    var splitText = new SplitText(splitTextElement, { type: "lines" });

    var anotherElement = currentSlide.querySelector(".split-text2");
    var splitAnotherElement = new SplitText(anotherElement, {
      type: "lines,words,chars",
    });

    var anotherElement2 = currentSlide.querySelector(".intro");
    var splitAnotherElement2 = new SplitText(anotherElement2, {
      type: "lines,words,chars",
    });

    var tl = gsap.timeline();
    tl.from(splitText.lines, {
      opacity: 0,
      y: 50,
      ease: "power3.out",
      stagger: 0.1,
      duration: 0.75,
      delay: 0.25,
    });

    tl.from(
      splitAnotherElement.chars,
      {
        opacity: 0,
        scale: 0.5,
        ease: "power3.out",
        stagger: 0.05, // Adjust stagger value as needed
        duration: 1, // Adjust duration as needed
        delay: 0.25,
      },
      "0"
    );

    tl.from(
      splitAnotherElement2.chars,
      {
        opacity: 0,
        y: 10,
        ease: "back",
        stagger: 0.01,
        duration: 0.5,
        delay: 0.25,
      },
      "0"
    );

    var arrowElement = currentSlide.querySelector(".arrow");
    console.log("Arrow Element:", arrowElement);

    tl.fromTo(
      arrowElement,
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 1 },
      "=-2.5"
    );
  });
};

export { featuresSwiper };

const attendeesSwiper = () => {
  document.querySelectorAll(".ps-attendees-swiper").forEach((el) => {
    const section = el.closest("section");
    const container = section && section.querySelector(".ps-attendees-container");

    const initSwiper = () => {
      const prevEl = section && section.querySelector(".ps-attendees-prev");
      const nextEl = section && section.querySelector(".ps-attendees-next");

      const paginationEl = section && section.querySelector(".ps-attendees-pagination");

      const swiper = new Swiper(el, {
        modules: [Navigation, Pagination],
        draggable: true,
        loop: true,
        slidesPerView: 1.3,
        spaceBetween: 16,
        breakpoints: {
          640: { slidesPerView: 2.2, spaceBetween: 20 },
          1024: { slidesPerView: 3.5, spaceBetween: 24 },
          1280: { slidesPerView: 4.2, spaceBetween: 24 },
        },
        pagination: {
          el: paginationEl,
          type: "progressbar",
        },
        navigation: { prevEl, nextEl },
      });

      disablePrevUntilNextUsed(swiper);

    };

    if (document.readyState === "complete") {
      initSwiper();
    } else {
      window.addEventListener("load", initSwiper, { once: true });
    }
  });
};

export { attendeesSwiper };

document.addEventListener("DOMContentLoaded", () => {
  let socialProofSwiper = null;
  const socialProofEl = document.querySelector(".social-proof-logos");

  const initSocialProofSwiper = () => {
    if (!socialProofEl || socialProofSwiper) return;

    socialProofSwiper = new Swiper(".social-proof-logos", {
      modules: [Navigation],
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      speed: 300,
      allowTouchMove: true,
      resistanceRatio: 0.85,
      navigation: {
        nextEl: ".social-proof-logos .swiper-button-next",
        prevEl: ".social-proof-logos .swiper-button-prev",
        addIcons: false, // use only the custom SVG arrows from the template
      },
    });
    disablePrevUntilNextUsed(socialProofSwiper);
  };

  const destroySocialProofSwiper = () => {
    if (!socialProofSwiper) return;
    socialProofSwiper.destroy(true, true);
    socialProofSwiper = null;
  };

  const checkSocialProofScreen = () => {
    if (!socialProofEl) return;
    const isMobile = window.matchMedia("(max-width: 1023px)").matches;

    if (isMobile && !socialProofSwiper) {
      initSocialProofSwiper();
    } else if (!isMobile && socialProofSwiper) {
      destroySocialProofSwiper();
    }
  };

  checkSocialProofScreen();
  window.addEventListener("resize", checkSocialProofScreen);
});
